import { Component, OnInit, HostListener } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { BillingSettingsService } from "../billing/settings/billing-settings.service";
import { BillingSettings } from "../settings/settings.interface";
import { PlanRow, FEATURE_ROWS } from "./subscription-data.component";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { DateFormatService } from "../shared/date-format.service";

@Component({
  selector: "app-subscription",
  templateUrl: "./subscription.component.html",
  styleUrls: ["./subscription.component.scss"],
  standalone: false,
})
export class SubscriptionComponent implements OnInit {
  currentPlan = "";
  isYearly = false;
  isMobile = false;
  billingSettings: BillingSettings | null = null;
  isLoading = true;
  isKeyFeaturesExpanded = false;
  mobileKeyFeaturesExpanded: { [planName: string]: boolean } = {};
  selectedCurrency = "USD";
  availableCurrencies = ["USD", "EUR", "PLN"];

  constructor(
    private billingSettingsService: BillingSettingsService,
    private router: Router,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private dateFormatService: DateFormatService,
  ) {}

  featureRows = FEATURE_ROWS;

  trackByRow(index: number, row: PlanRow): string {
    return row.feature;
  }

  ngOnInit() {
    this.checkScreenSize();
    this.loadBillingSettings();
  }

  @HostListener("window:resize", ["$event"])
  onResize = () => {
    this.checkScreenSize();
  };

  checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
  }

  toggleKeyFeatures() {
    this.isKeyFeaturesExpanded = !this.isKeyFeaturesExpanded;
  }

  toggleMobileKeyFeatures(planName: string) {
    this.mobileKeyFeaturesExpanded[planName] =
      !this.mobileKeyFeaturesExpanded[planName];
  }

  shouldShowRow(row: PlanRow, index: number): boolean {
    if (row.feature === "subscription.keyFeaturesInDetail") {
      return true;
    }

    const keyFeaturesIndex = this.featureRows.findIndex(
      (r) => r.feature === "subscription.keyFeaturesInDetail",
    );
    if (index > keyFeaturesIndex) {
      return this.isKeyFeaturesExpanded;
    }

    return true;
  }

  shouldShowMobileRow(row: PlanRow, index: number, planName: string): boolean {
    if (row.feature === "subscription.keyFeaturesInDetail") {
      return true;
    }

    const keyFeaturesIndex = this.featureRows.findIndex(
      (r) => r.feature === "subscription.keyFeaturesInDetail",
    );
    if (index > keyFeaturesIndex) {
      return this.mobileKeyFeaturesExpanded[planName];
    }

    return true;
  }

  manageSubscription() {
    void this.router.navigate(["/settings/billing"]);
  }

  subscribeToPlan(planName: string) {
    if (this.isCurrentPlan(planName)) {
      return;
    }

    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(1);
    const nextMonthDate = this.dateFormatService.formatDate(nextMonth);

    const dialogData: {
      body: string;
      confirmButton: string;
      additionalInfo?: string;
    } = {
      body: this.translateService.instant(
        "subscription.changePlanConfirmation.message",
        { planName },
      ) as string,
      confirmButton: this.translateService.instant(
        "subscription.changePlanConfirmation.confirm",
      ) as string,
    };

    if (
      this.billingSettings.planId !== "free" &&
      this.billingSettings.planId !== "freemium"
    ) {
      dialogData.additionalInfo = this.translateService.instant(
        "subscription.changePlanConfirmation.effectiveDate",
        { date: nextMonthDate },
      ) as string;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.processPlanChange(planName);
      }
    });
  }

  private processPlanChange(planName: string) {
    if (this.billingSettings) {
      const planNames =
        planName.toLowerCase() === "free" ? "freemium" : planName.toLowerCase();

      const planPrices = this.getBillingPlanPrices(
        planNames,
        this.selectedCurrency,
      );
      const calculatedPrice = this.isYearly
        ? planPrices.yearly
        : planPrices.monthly;

      if (
        this.billingSettings.planId === "free" ||
        this.billingSettings.planId === "freemium"
      ) {
        const updatedSettings = {
          ...this.billingSettings,
          planName: planNames,
          subscriptionPeriod: this.isYearly ? "yearly" : "monthly",
          currency: this.selectedCurrency,
          price: calculatedPrice,
        };

        this.billingSettingsService.update(updatedSettings).subscribe({
          next: (response) => {
            this.billingSettings = response;
            this.currentPlan = planName;
            void this.router.navigate(["/settings/billing"]);
          },
        });
      } else {
        this.billingSettingsService
          .savePendingPlanChange({
            planName: planNames,
            subscriptionPeriod: this.isYearly ? "yearly" : "monthly",
            currency: this.selectedCurrency,
            price: calculatedPrice,
          })
          .subscribe({
            next: (response) => {
              this.billingSettings = response;
              this.currentPlan = planName;
              void this.router.navigate(["/settings/billing"]);
            },
          });
      }
    }
  }

  scrollTable(direction: "left" | "right") {
    const container = document.querySelector(".desktop-view");
    if (container) {
      container.scrollBy({
        left: direction === "left" ? -220 : 220,
        behavior: "smooth",
      });
    }
  }

  canScroll(direction: "left" | "right"): boolean {
    const container = document.querySelector(".desktop-view");
    if (!container) {
      return false;
    }

    if (direction === "left") {
      return container.scrollLeft > 0;
    } else {
      const maxScroll = container.scrollWidth - container.clientWidth;
      return container.scrollLeft < maxScroll - 1;
    }
  }

  loadBillingSettings() {
    this.billingSettingsService.get().subscribe({
      next: (settings) => {
        this.billingSettings = settings;
        this.currentPlan =
          settings.planId === "freemium" ? "Free" : settings.planName || "Free";
        this.isYearly = settings.subscriptionPeriod === "yearly";
        this.selectedCurrency = settings.currency || "USD";
        this.isLoading = false;
      },
      error: (_error) => {
        this.currentPlan = "";
        this.isYearly = false;
        this.selectedCurrency = "USD";
        this.isLoading = false;
      },
    });
  }

  isCurrentPlan(planName: string): boolean {
    return this.currentPlan?.toLowerCase() === planName.toLowerCase();
  }

  getPlanPrice(planName: string): string {
    const prices = this.getBillingPlanPrices(planName, this.selectedCurrency);
    const price = this.isYearly ? prices.yearly : prices.monthly;
    return `${price} ${this.selectedCurrency}`;
  }

  getPlanDescription(planName: string): string {
    const planKey = planName.toLowerCase();
    return this.translateService.instant(
      `subscription.planDescriptions.${planKey}`,
    ) as string;
  }

  getTranslatedValue(value: string): string {
    if (value === "yes" || value === "" || value === "-") {
      return value;
    }
    if (value.startsWith("subscription.")) {
      return this.translateService.instant(value) as string;
    }
    return value;
  }

  onCurrencyChange(newCurrency: string) {
    this.selectedCurrency = newCurrency;
  }

  onBillingPeriodChange(period: string) {
    this.isYearly = period === "yearly";
  }

  private getBillingPlanPrices(
    planName: string | null | undefined,
    currency: string = "USD",
  ): { monthly: number; yearly: number } {
    const isPLN = currency.toUpperCase() === "PLN";

    switch (planName?.toLowerCase()) {
      case "freemium":
      case "free":
        return { monthly: 0, yearly: 0 };
      case "basic":
        return { monthly: isPLN ? 3.99 : 0.99, yearly: isPLN ? 3.59 : 0.89 };
      case "premium":
        return { monthly: isPLN ? 7.99 : 1.99, yearly: isPLN ? 7.19 : 1.79 };
      default:
        return { monthly: 0, yearly: 0 };
    }
  }
}
